<ph-dialog open heading="Send payment for invoice" (ph-close)="cancel()">
    <app-payable-review
        [payable]="data.payable"
        [useContainer]="false"
        [displayPaymentDetails]="false"
    >
    </app-payable-review>

    <ph-divider></ph-divider>

    <form [formGroup]="paymentForm">
        <div class="payment-details-heading">Payment details</div>

        <ph-field>
            <ph-label>Payment method</ph-label>
            <ng-container *ngIf="isLoadingPaymentMethod; else loadedPaymentMethod">
                <ph-skeleton shape="list" rows="3"></ph-skeleton>
            </ng-container>
            <ng-template #loadedPaymentMethod>
                <ph-radio-group formControlName="paymentMethod" ngDefaultControl [items]="paymentMethodItems"></ph-radio-group>
                <lf-control-messages hint [control]="paymentMethod"></lf-control-messages>
            </ng-template>
        </ph-field>

        <ng-container *ngIf="!isLoadingPaymentMethod">
            <!-- SD-11999: the ACH gate could not be read, so ACH is hidden. Distinguish that from
                 an organization that genuinely has no Treasury account, which looks the same. -->
            <ph-hint *ngIf="treasuryGateUnreadable">
                We couldn't check whether ACH is available right now. Refresh to try again.
            </ph-hint>
            <ng-container *ngFor="let option of paymentMethodOptions; trackBy: trackByOptionValue">
                <ng-container *ngIf="option.visible && option.hint" [ngSwitch]="option.hint">
                    <ph-hint *ngSwitchCase="'vendorAch'">
                        This vendor isn't set up for ACH yet. Enable ACH in
                        <ph-link-button inline variant="primary" (click)="navigateToVendorSettings($event)">vendor settings</ph-link-button>.
                    </ph-hint>
                    <ph-hint *ngSwitchCase="'vendorAchUnreadable'">
                        ACH isn't available because this vendor's bank accounts couldn't be read. Ask an admin with financial account access to send this payment.
                    </ph-hint>
                    <ph-hint *ngSwitchCase="'vendorAchUnavailable'">
                        ACH isn't available right now — this vendor's bank accounts couldn't be loaded. Close and reopen this dialog to try again.
                    </ph-hint>
                    <ph-hint *ngSwitchCase="'achExceedsBalance'" has-error>
                        ACH transfer: amount exceeds available balance
                    </ph-hint>
                    <ph-hint *ngSwitchCase="'achSwitchedOff'">
                        Please contact PayHOA to enable ACH transfers for this organization.
                    </ph-hint>
                    <ph-hint *ngSwitchCase="'verifyBillPay'">
                        You must {{ lobBankAccounts.length === 0 ? 'create and' : '' }} verify a
                        <a href="/app/payables/settings/bill-pay-bank-accounts">bill pay account</a>
                        to mail a check.
                    </ph-hint>
                    <ph-hint *ngSwitchCase="'zeroAmount'">
                        Not available for $0.
                    </ph-hint>
                    <ph-alert *ngSwitchCase="'vendorNameTooLong'" status="warning" variant="lighter" data-test="vendor-name-too-long-alert">
                        <span>A mailed check can print at most 40 characters of the vendor name, and
                        "{{ data.payable.vendor?.name }}" is longer. Shorten it or move part (such as a DBA) to Nickname in
                        <ph-link-button inline variant="primary" (click)="navigateToVendorSettings($event)">vendor settings</ph-link-button>
                        to mail a check.</span>
                    </ph-alert>
                </ng-container>
            </ng-container>

            <ph-alert *ngIf="!hasSelectableMethod" status="warning" data-test="no-payment-method-alert">
                <strong>No payment method is available for this payable yet.</strong>
                <ng-container *ngIf="!hasVerifiedBillPayAccount">
                    <br>
                    You must {{ lobBankAccounts.length === 0 ? 'create and' : '' }} verify a
                    <a href="/app/payables/settings/bill-pay-bank-accounts">bill pay account</a>
                    to mail a check.
                </ng-container>
            </ph-alert>
        </ng-container>

        <ph-field *ngIf="showMailCheckOptions">
            <ph-label>Bank account</ph-label>
            <app-lob-bank-account-selector
                [formGroup]="paymentForm"
                [lobBankAccounts]="verifiedBillPayAccounts"
                controlName="bankAccountId"
            ></app-lob-bank-account-selector>
            <app-check-number-input
                [formGroup]="paymentForm"
                controlName="checkNumber"
                bankAccountControlName="bankAccountId"
                [bankAccounts]="verifiedBillPayAccounts"
            ></app-check-number-input>
        </ph-field>

        <ng-container *ngIf="showAchOptions">
            <ph-field *ngIf="vendorBankAccounts.length > 1">
                <ph-label required>Vendor bank account</ph-label>
                <ph-select formControlName="vendorBankAccountId" ngDefaultControl [items]="vendorBankAccountItems"></ph-select>
            </ph-field>
            <ph-alert *ngIf="selectedAchBankAccount" status="information">
                Payment will be sent via ACH from your Treasury Balance account to {{ data.payable.vendor?.name }}'s bank account ending in ****{{ selectedAchBankAccount.last4 }}.
            </ph-alert>
            <lf-control-messages hint [control]="vendorBankAccount"></lf-control-messages>
        </ng-container>

        <ph-field>
            <ph-label>Schedule payment date</ph-label>
            <ph-date-input formControlName="scheduledPaymentDate" ngDefaultControl [min]="minDate"></ph-date-input>
            <lf-control-messages hint [control]="scheduledPaymentDate"></lf-control-messages>
        </ph-field>
    </form>

    <ph-actions slot="footer" align="end">
        <ph-button variant="secondary" mode="filled" type="button" aria-label="cancel" (click)="cancel()">Cancel</ph-button>
        <ph-button variant="primary"
                   mode="filled"
                   type="button"
                   [disabled]="isLoadingPaymentMethod || isSubmitting || !hasSelectableMethod"
                   (click)="confirmAndSchedulePayment()">{{ isSubmitting ? 'Scheduling payment…' : 'Confirm & schedule payment' }}</ph-button>
    </ph-actions>
</ph-dialog>

<app-approval-required-dialog [open]="approvalRequiredOpen" (closed)="approvalRequiredOpen = false"></app-approval-required-dialog>
